/**
 * Z-Index Tokens for EroChat Design System
 *
 * Provides a consistent z-index hierarchy to ensure
 * proper stacking of UI elements across the application.
 *
 * Hierarchy (lowest to highest):
 * 1. Base content (0)
 * 2. Dropdowns (10)
 * 3. Sticky headers (20)
 * 4. Overlays/backdrops (30)
 * 5. Modals/dialogs (40)
 * 6. Navigation (50)
 * 7. Toast notifications (60)
 * 8. Tooltips (70)
 *
 * @module design-system/tokens/z-index
 */

/**
 * Z-Index token values interface
 */
export interface ZIndexTokens {
  /** Hidden - below everything (-1) */
  hidden: number;
  /** Base level - normal content flow (0) */
  base: number;
  /** Dropdown menus and select popups (10) */
  dropdown: number;
  /** Sticky elements like headers (20) */
  sticky: number;
  /** Overlay backgrounds/backdrops (30) */
  overlay: number;
  /** Modal dialogs and sheets (40) */
  modal: number;
  /** Main navigation elements (50) */
  navigation: number;
  /** Toast notifications (60) */
  toast: number;
  /** Tooltips - always on top (70) */
  tooltip: number;
  /** Maximum - emergency override (9999) */
  max: number;
}

/**
 * Default z-index values following a consistent hierarchy.
 *
 * Uses increments of 10 to allow for intermediate values
 * when absolutely necessary (though this should be rare).
 *
 * @example
 * ```tsx
 * import { defaultZIndex } from '@/design-system/tokens/z-index';
 *
 * const Modal = () => (
 *   <>
 *     <div style={{ zIndex: defaultZIndex.overlay }} className="backdrop" />
 *     <div style={{ zIndex: defaultZIndex.modal }} className="modal-content">
 *       Content
 *     </div>
 *   </>
 * );
 * ```
 */
export const defaultZIndex: ZIndexTokens = {
  /**
   * Hidden (-1)
   * For elements that should be below everything
   * Best for: hidden content, backgrounds
   */
  hidden: -1,

  /**
   * Base (0)
   * Normal document flow
   * Best for: most content, default stacking
   */
  base: 0,

  /**
   * Dropdown (10)
   * Popup menus and select dropdowns
   * Best for: dropdown menus, autocomplete, popovers
   */
  dropdown: 10,

  /**
   * Sticky (20)
   * Sticky positioned elements
   * Best for: sticky headers, sub-navigation, floating action buttons
   */
  sticky: 20,

  /**
   * Overlay (30)
   * Modal backdrops and overlays
   * Best for: modal backgrounds, sheet overlays, lightbox backgrounds
   */
  overlay: 30,

  /**
   * Modal (40)
   * Modal content above overlays
   * Best for: modal dialogs, bottom sheets, side panels
   */
  modal: 40,

  /**
   * Navigation (50)
   * Primary navigation should be above modals
   * Best for: main nav, mobile nav drawer, app header
   */
  navigation: 50,

  /**
   * Toast (60)
   * Toast notifications above navigation
   * Best for: toast messages, snackbars, alerts
   */
  toast: 60,

  /**
   * Tooltip (70)
   * Tooltips always on top
   * Best for: tooltips, hints, help overlays
   */
  tooltip: 70,

  /**
   * Max (9999)
   * Emergency override - use sparingly!
   * Best for: critical system overlays, loading screens
   */
  max: 9999,
};

/**
 * Helper to get a z-index CSS variable reference
 *
 * @param key - Z-index token key
 * @returns CSS variable reference string
 *
 * @example
 * ```tsx
 * const zIndex = getZIndexVar('modal');
 * // Returns: 'var(--z-index-modal)'
 * ```
 */
export const getZIndexVar = (key: keyof ZIndexTokens): string => {
  return `var(--z-index-${key})`;
};

/**
 * Converts z-index tokens to CSS custom properties
 *
 * @param zIndex - Z-index tokens object
 * @returns Object with CSS variable names as keys
 *
 * @example
 * ```ts
 * const cssVars = zIndexToCssVars(defaultZIndex);
 * // { '--z-index-base': '0', '--z-index-dropdown': '10', ... }
 * ```
 */
export const zIndexToCssVars = (zIndex: ZIndexTokens): Record<string, string> => {
  return Object.entries(zIndex).reduce<Record<string, string>>((acc, [key, value]) => {
    acc[`--z-index-${key}`] = String(value);
    return acc;
  }, {});
};

/**
 * Type-safe z-index class name generator for Tailwind
 *
 * Since Tailwind has limited z-index utilities, this provides
 * a mapping to CSS variable-based styles.
 *
 * @example
 * ```tsx
 * // Instead of: className="z-[40]"
 * // Use: style={{ zIndex: defaultZIndex.modal }}
 * ```
 */
export const zIndexStyles = {
  hidden: { zIndex: defaultZIndex.hidden },
  base: { zIndex: defaultZIndex.base },
  dropdown: { zIndex: defaultZIndex.dropdown },
  sticky: { zIndex: defaultZIndex.sticky },
  overlay: { zIndex: defaultZIndex.overlay },
  modal: { zIndex: defaultZIndex.modal },
  navigation: { zIndex: defaultZIndex.navigation },
  toast: { zIndex: defaultZIndex.toast },
  tooltip: { zIndex: defaultZIndex.tooltip },
  max: { zIndex: defaultZIndex.max },
} as const;
